import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";

import { useServer } from "../../server/ServerContext";
import { PortalAxiosError } from "../../types";
import { OfferData, OffersData, UseOfferParams } from "./types";
import { offersKeys } from "./utils";

/**
 * Fetch a single offer
 * @param offerIdentifier offer identifier
 * @param options useQuery options
 * @returns useQuery result
 */
export const useOffer = <TData extends {} = OfferData>(
  offerIdentifier: string,
  params?: UseOfferParams,
  options?: UseQueryOptions<OfferData, PortalAxiosError, TData>
) => {
  const server = useServer();
  const queryClient = useQueryClient();

  return useQuery<OfferData, PortalAxiosError, TData>(
    offersKeys.getOne(offerIdentifier),
    () => server.get(`offers/${offerIdentifier}`, { params }),
    {
      initialData: () => {
        const offers = queryClient.getQueryData<OffersData>(offersKeys.list);

        const offer = offers?.data.find((offer) => {
          return offer.identifier === offerIdentifier;
        });

        return offer ? { data: offer } : undefined;
      },
      initialDataUpdatedAt: () => {
        return queryClient.getQueryState(offersKeys.list)?.dataUpdatedAt;
      },
      staleTime: 2 * 60 * 1000,
      ...options,
    }
  );
};

export default useOffer;
